// packages/backoffice-ui/src/ui/modal.tsx
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './dialog';
import { Button } from './button';
import { cn } from './cn';
import { t } from '@heyroot/ui/i18n';
import { useEffect, useRef, type ReactNode } from 'react';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
  ariaLabel?: string;
  /** Dialog role. Use 'alertdialog' for destructive / confirmation dialogs (GATE B.1). */
  role?: 'dialog' | 'alertdialog';
  /**
   * Panel width. 'default' (max-w-lg) suits forms and confirmations. 'wide'
   * (max-w-4xl) exists for media viewers, where the default is narrower than
   * the content being examined -- an image lightbox capped at 32rem defeats
   * its own purpose. Additive: omitting this preserves the previous width.
   */
  size?: 'default' | 'wide';
  /** Optional class for the rendered dialog panel. */
  contentClassName?: string;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  closeOnBackdrop = true,
  ariaLabel,
  role = 'dialog',
  size = 'default',
  contentClassName,
}: ModalProps) {
  // None of this Modal's call sites render a <Dialog.Trigger> (open is driven
  // by an external boolean prop), so Radix has no trigger to auto-restore
  // focus to on close. Replicate the old hand-rolled implementation's
  // capture-on-open / restore-on-close behavior explicitly.
  const previouslyFocused = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        role={role}
        // The pre-Radix Modal had no corner X: dismissal was Escape, the
        // backdrop, and whatever the call site put in its footer or header.
        // Keeping DialogContent's corner Close here would add a second
        // "Close"-named control to every call site that already renders its own
        // header dismiss button (e.g. employee-portal's NewMessageLocationPicker).
        showCloseButton={false}
        className={cn(
          size === 'wide'
            ? 'max-h-[80vh] overflow-y-auto max-w-4xl'
            : 'max-h-[80vh] overflow-y-auto',
          contentClassName,
        )}
        onPointerDownOutside={(e) => {
          if (!closeOnBackdrop) e.preventDefault();
        }}
        onOpenAutoFocus={(e) => {
          // Preserve the old implementation's exact fallback: focus the
          // [data-autofocus] element if present, otherwise the dialog
          // container itself -- never Radix's own default of "first
          // focusable descendant" (which would land on the Close button,
          // one Enter/Space keypress away from dismissing the dialog
          // immediately after it opens).
          e.preventDefault();
          const container = e.currentTarget as HTMLElement;
          const target = container.querySelector<HTMLElement>('[data-autofocus]');
          (target ?? container).focus();
        }}
        onCloseAutoFocus={(e) => {
          if (previouslyFocused.current) {
            e.preventDefault();
            previouslyFocused.current.focus();
          }
        }}
        aria-label={!title ? ariaLabel : undefined}
      >
        {title ? (
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        ) : (
          // Radix requires a Title for the dialog's accessible name, but a call
          // site that passes ariaLabel instead of title usually renders its own
          // visible heading with the same words (e.g. employee-portal's
          // NewMessageLocationPicker). Rendering this fallback as a <span>
          // rather than DialogTitle's default <h2> keeps the accessible name
          // without adding a second heading to the document.
          <DialogTitle asChild>
            <span className="sr-only">{ariaLabel ?? 'Dialog'}</span>
          </DialogTitle>
        )}
        {children}
        <DialogFooter>
          {footer ?? (
            <Button variant="ghost" onClick={onClose}>
              {t('modal.close')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
